import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Typography, Button, Space, Tag, Spin, Divider } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AppDispatch, CombinedStates } from '../../core/types';
import { clearCategoryData } from '../../core/bvm/bvmSlice';
import { BvmCategoryQuestion } from '../../core/bvm/types';
import './style.scss';

const { Title, Text, Paragraph } = Typography;

const SchoolBvmCategoryData: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();

    const {
        singleCategory,
        categoryPeriod,
        categoryDate,
        categorySchoolId,
        isFetching,
        error,
    } = useSelector((state: CombinedStates) => state.bvm);

    const handleBackToCategories = () => {
        dispatch(clearCategoryData());
    };

    const getStatusIcon = (isFilled: boolean) => (isFilled
        ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
        : <ClockCircleOutlined style={{ color: '#faad14' }} />);

    const getStatusColor = (isFilled: boolean) => (isFilled ? 'success' : 'warning');

    const getStatusText = (isFilled: boolean) => (isFilled ? 'Užpildyta' : 'Neužpildyta');

    const renderQuestion = (question: BvmCategoryQuestion) => {
        if (question.type === 'paragraph') {
            return (
                <div key={question.id} className="paragraph-question">
                    <Text strong>{`${question.code} ${question.title}`}</Text>
                    {question.description && (
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">{question.description}</Text>
                        </div>
                    )}
                </div>
            );
        }

        if (question.type === 'text') {
            return (
                <div key={question.id} className="text-question">
                    <div className="question-header">
                        <Text strong>{`${question.code} ${question.title}`}</Text>
                        {question.description && (
                            <div style={{ marginTop: 4 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {question.description}
                                </Text>
                            </div>
                        )}
                    </div>
                    <div className="question-answer">
                        <Paragraph style={{
                            background: '#f5f5f5',
                            padding: '12px',
                            borderRadius: '6px',
                            marginTop: '8px',
                            marginBottom: 0,
                        }}>
                            {question.answer || 'Atsakymas nepateiktas'}
                        </Paragraph>
                    </div>
                </div>
            );
        }

        return null;
    };

    if (isFetching) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    <Text>Kraunami kategorijos duomenys...</Text>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text type="danger">Įvyko klaida kraunant kategorijos duomenis</Text>
                </div>
            </Card>
        );
    }

    if (!singleCategory) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text type="secondary">Kategorijos duomenys nerasti</Text>
                </div>
            </Card>
        );
    }

    return (
        <div className="school-bvm-category-data">
            <Card>
                <div className="category-header">
                    <Button
                        onClick={handleBackToCategories}
                        icon={<ArrowLeftOutlined />}
                        style={{ marginBottom: 16 }}>
                        Grįžti į kategorijas
                    </Button>

                    <Title level={3}>{singleCategory.title}</Title>

                    <Space wrap>
                        <Tag color={getStatusColor(singleCategory.is_filled)}>
                            {getStatusText(singleCategory.is_filled)}
                        </Tag>
                        {singleCategory.submitted_at && (
                            <Text type="secondary">
                                Pateikta: {dayjs(singleCategory.submitted_at).format('YYYY-MM-DD HH:mm')}
                            </Text>
                        )}
                    </Space>

                    {singleCategory.description && (
                        <div style={{ marginTop: 12 }}>
                            <Text type="secondary">{singleCategory.description}</Text>
                        </div>
                    )}

                    <div className="category-meta">
                        <Row gutter={16}>
                            <Col span={8}>
                                <Text strong>Laikotarpis: </Text>
                                <Text>{categoryPeriod}</Text>
                            </Col>
                            <Col span={8}>
                                <Text strong>Data: </Text>
                                <Text>{categoryDate}</Text>
                            </Col>
                            <Col span={8}>
                                <Text strong>Mokyklos ID: </Text>
                                <Text>{categorySchoolId}</Text>
                            </Col>
                        </Row>
                    </div>
                </div>

                <Divider />

                <div className="questions-section">
                    <Title level={4}>Klausimai ir atsakymai</Title>
                    {singleCategory.questions && singleCategory.questions.length > 0 ? (
                        <div className="questions-list">
                            {[...singleCategory.questions]
                                .sort((a, b) => a.order - b.order)
                                .map(renderQuestion)}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '30px' }}>
                            <Text type="secondary">Nėra klausimų šioje kategorijoje</Text>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default SchoolBvmCategoryData;
